/**
 * Product Image Service
 * Handles all API calls for product image management
 */

import { apiService } from "./api";
import {
    ProductImage,
    ProductImageUpdate,
    ProductImageReorderRequest,
    SetDefaultImageRequest,
} from "../types/productImage";

class ProductImageService {
    /**
     * Upload a new product image
     */
    async uploadImage(productId: string, file: File): Promise<ProductImage> {
        const formData = new FormData();
        formData.append("file", file);

        return apiService.post<ProductImage>(
            `/products/${productId}/images`,
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
                timeout: 60000, // 60s for image upload + R2 processing
            },
        );
    }

    /**
     * Get all images for a product
     */
    async getProductImages(productId: string): Promise<ProductImage[]> {
        return apiService.get<ProductImage[]>(`/products/${productId}/images`);
    }

    /**
     * Get a specific product image
     */
    async getProductImage(
        productId: string,
        imageId: string,
    ): Promise<ProductImage> {
        return apiService.get<ProductImage>(
            `/products/${productId}/images/${imageId}`,
        );
    }

    /**
     * An image's bytes, through the API (R2 sends no CORS headers, so the
     * browser can't fetch the public URL itself) — used to download it.
     */
    async getImageBlob(
        productId: string,
        imageId: string,
        variant: "original" | "large" | "medium" | "thumbnail" = "original",
    ): Promise<Blob> {
        return apiService.get<Blob>(
            `/products/${productId}/images/${imageId}/file?variant=${variant}`,
            { responseType: "blob", timeout: 60000 },
        );
    }

    /**
     * Update product image metadata
     */
    async updateImage(
        productId: string,
        imageId: string,
        data: ProductImageUpdate,
    ): Promise<ProductImage> {
        return apiService.put<ProductImage>(
            `/products/${productId}/images/${imageId}`,
            data,
        );
    }

    /**
     * Delete a product image
     */
    async deleteImage(productId: string, imageId: string): Promise<void> {
        await apiService.delete(`/products/${productId}/images/${imageId}`);
    }

    /**
     * Reorder product images
     */
    async reorderImages(
        productId: string,
        imageOrders: ProductImageReorderRequest,
    ): Promise<ProductImage[]> {
        return apiService.put<ProductImage[]>(
            `/products/${productId}/images/reorder`,
            imageOrders,
        );
    }

    /**
     * Set an image as the default product image
     */
    async setDefaultImage(
        productId: string,
        imageId: string,
    ): Promise<ProductImage> {
        const request: SetDefaultImageRequest = { image_id: imageId };
        return apiService.put<ProductImage>(
            `/products/${productId}/images/set-default`,
            request,
        );
    }

    /**
     * Upload multiple images at once
     */
    async uploadMultipleImages(
        productId: string,
        files: File[],
    ): Promise<ProductImage[]> {
        const uploadPromises = files.map((file) =>
            this.uploadImage(productId, file),
        );
        return Promise.all(uploadPromises);
    }
}

export const productImageService = new ProductImageService();
